'use client';

import React from 'react';
import { config } from '@/src/config';
import styles from './Auth.module.css';

interface TwitterSignInButtonProps {
  label: string;
  referral?: string;
  disabled?: boolean;
}

const TWITTER_AUTH_ENDPOINT = 'https://twitter.com/i/oauth2/authorize';
const TWITTER_SCOPES = 'users.read tweet.read offline.access';
const VERIFIER_STORAGE_KEY = 'aceb_twitter_pkce_verifier';

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function generateCodeVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

const TwitterSignInButton: React.FC<TwitterSignInButtonProps> = ({ label, referral, disabled }) => {
  if (!config.TWITTER_CLIENT_ID) return null;

  const handleClick = async () => {
    const clientId = config.TWITTER_CLIENT_ID;
    const redirectUri = `${window.location.origin}/twitter`;

    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    sessionStorage.setItem(VERIFIER_STORAGE_KEY, verifier);

    const state = referral
      ? encodeURIComponent(JSON.stringify({ referral }))
      : generateCodeVerifier();

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      redirect_uri: redirectUri,
      scope: TWITTER_SCOPES,
      state,
      code_challenge: challenge,
      code_challenge_method: 'S256',
    });

    window.location.href = `${TWITTER_AUTH_ENDPOINT}?${params.toString()}`;
  };

  return (
    <button
      type="button"
      className={styles.twitterButton}
      onClick={handleClick}
      disabled={disabled}
      aria-label={label}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
        />
      </svg>
      <span>{label}</span>
    </button>
  );
};

export default TwitterSignInButton;
